<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import ReasonPromptModal from '../components/ReasonPromptModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatCount, formatMoney, toIsoDate } from '../utils/format'
import type { LaborAccrual, PayrollPeriod } from '../types/finance'

/**
 * `/payroll/accruals` — production labour accruals.
 * Lifecycle: provisional → approved → exported → paid, plus rejected/reversed.
 */

const { t } = useI18n()
const auth = useAuthStore()
const {
  listLaborAccruals,
  listPayrollPeriods,
  approveLaborAccrual,
  rejectLaborAccrual,
  reverseLaborAccrual,
} = useFinanceApi()

const PER_PAGE = 25
const permissions = computed(() => auth.user?.permissions ?? [])
const canApprove = computed(() => permissions.value.includes('payroll_accruals.approve'))
const canReverse = computed(() => permissions.value.includes('payroll_accruals.reverse'))

const rows = ref<LaborAccrual[]>([])
const periods = ref<PayrollPeriod[]>([])
const total = ref(0)
const totalPages = ref(1)
const page = ref(1)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<string | number | null>(null)

const filters = ref({
  payroll_period_id: '' as string | number,
  status: 'provisional',
  currency_code: '',
  date_from: '',
  date_to: '',
})

const pending = ref<{ action: 'reject' | 'reverse'; accrual: LaborAccrual } | null>(null)

const totalsByCurrency = computed(() => {
  const totals = new Map<string, { gross: number; count: number }>()
  for (const row of rows.value) {
    const code = row.currency_code || '—'
    const current = totals.get(code) ?? { gross: 0, count: 0 }
    current.gross += Number(row.gross_amount ?? 0)
    current.count += 1
    totals.set(code, current)
  }
  return [...totals.entries()]
    .map(([code, value]) => ({ code, ...value }))
    .sort((a, b) => Math.abs(b.gross) - Math.abs(a.gross))
})

const quantityTotals = computed(() => ({
  completed: rows.value.reduce((sum, row) => sum + Number(row.quantity_completed ?? 0), 0),
  accepted: rows.value.reduce((sum, row) => sum + Number(row.quantity_accepted ?? 0), 0),
  rejected: rows.value.reduce((sum, row) => sum + Number(row.quantity_rejected ?? 0), 0),
  rework: rows.value.reduce((sum, row) => sum + Number(row.quantity_rework ?? 0), 0),
}))

const rangeLabel = computed(() =>
  total.value ? t('finance.common.rows', { count: total.value }) : '',
)

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.accrualStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listLaborAccruals({
      payroll_period_id: filters.value.payroll_period_id,
      status: filters.value.status || undefined,
      currency_code: filters.value.currency_code.trim() || undefined,
      date_from: filters.value.date_from || undefined,
      date_to: filters.value.date_to || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function loadPeriods() {
  try {
    const result = await listPayrollPeriods({ page: 1, per_page: 50 })
    periods.value = result.items
  } catch {
    periods.value = []
  }
}

async function approve(accrual: LaborAccrual) {
  busyId.value = accrual.id
  actionError.value = ''
  actionMessage.value = ''
  try {
    await approveLaborAccrual(accrual.id)
    actionMessage.value = t('finance.payrollFlow.accruals.doneApproved', { employee: accrual.employee_name })
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

async function confirmReason(reason: string) {
  const pendingAction = pending.value
  if (!pendingAction) return
  busyId.value = pendingAction.accrual.id
  actionError.value = ''
  actionMessage.value = ''
  try {
    if (pendingAction.action === 'reject') {
      await rejectLaborAccrual(pendingAction.accrual.id, reason)
      actionMessage.value = t('finance.payrollFlow.accruals.doneRejected')
    } else {
      await reverseLaborAccrual(pendingAction.accrual.id, reason)
      actionMessage.value = t('finance.payrollFlow.accruals.doneReversed')
    }
    pending.value = null
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

watch(
  () => [
    filters.value.payroll_period_id,
    filters.value.status,
    filters.value.currency_code,
    filters.value.date_from,
    filters.value.date_to,
  ],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void loadPeriods()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.accruals.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.accruals.title') }}</h2>
      <p>{{ t('finance.payrollFlow.accruals.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.payroll_period_id" class="filter-select">
      <option value="">{{ t('finance.payrollFlow.allPeriods') }}</option>
      <option v-for="period in periods" :key="period.id" :value="period.id">
        {{ period.code }} · {{ period.name }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="provisional">{{ statusLabel('provisional') }}</option>
      <option value="approved">{{ statusLabel('approved') }}</option>
      <option value="rejected">{{ statusLabel('rejected') }}</option>
      <option value="exported">{{ statusLabel('exported') }}</option>
      <option value="paid">{{ statusLabel('paid') }}</option>
      <option value="reversed">{{ statusLabel('reversed') }}</option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <article v-for="total in totalsByCurrency" :key="total.code" class="summary-card accent">
        <span class="summary-value">{{ formatMoney(total.gross, total.code) }}</span>
        <span class="summary-label">
          {{ t('finance.payrollFlow.accruals.gross') }} · {{ t('finance.common.rows', { count: total.count }) }}
        </span>
      </article>
      <article class="summary-card">
        <span class="summary-value">
          {{ formatCount(quantityTotals.accepted) }}
          <small>/ {{ t('finance.payrollFlow.accruals.completed') }} {{ formatCount(quantityTotals.completed) }}</small>
        </span>
        <span class="summary-label">
          {{ t('finance.payrollFlow.accruals.rejected') }} {{ formatCount(quantityTotals.rejected) }} ·
          {{ t('finance.payrollFlow.accruals.rework') }} {{ formatCount(quantityTotals.rework) }}
        </span>
      </article>
    </div>

    <p v-if="!rows.length" class="state-message">{{ t('finance.payrollFlow.accruals.empty') }}</p>

    <template v-else>
      <div class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th>{{ t('finance.payrollFlow.accruals.employee') }}</th>
              <th>{{ t('finance.payrollFlow.accruals.production') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.accruals.completed') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.accruals.accepted') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.accruals.rate') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.accruals.gross') }}</th>
              <th>{{ t('finance.common.status') }}</th>
              <th class="cell-actions">{{ t('finance.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="accrual in rows" :key="accrual.id">
              <td class="cell-name">
                {{ accrual.employee_name }}
                <span class="cell-sub">{{ accrual.employee_code || '—' }}</span>
              </td>
              <td>
                {{ accrual.product_name || '—' }}
                <span class="cell-sub">
                  {{ accrual.payroll_period_code || '—' }} ·
                  {{ accrual.stage_code || '—' }} ·
                  {{ accrual.lot_number || '—' }}
                </span>
              </td>
              <td class="cell-num">{{ formatCount(accrual.quantity_completed) }}</td>
              <td class="cell-num">{{ formatCount(accrual.quantity_accepted) }}</td>
              <td class="cell-num">{{ formatMoney(accrual.rate_amount, accrual.currency_code) }}</td>
              <td class="cell-num">{{ formatMoney(accrual.gross_amount, accrual.currency_code) }}</td>
              <td>
                <span class="status-badge" :class="`status-${accrual.status}`">
                  {{ statusLabel(accrual.status) }}
                </span>
              </td>
              <td class="cell-actions">
                <button
                  v-if="canApprove && accrual.status === 'provisional'"
                  class="link-action"
                  type="button"
                  :disabled="busyId === accrual.id"
                  @click="approve(accrual)"
                >
                  {{ t('finance.payrollFlow.approve') }}
                </button>
                <button
                  v-if="canApprove && accrual.status === 'provisional'"
                  class="link-action danger"
                  type="button"
                  :disabled="busyId === accrual.id"
                  @click="pending = { action: 'reject', accrual }"
                >
                  {{ t('finance.payrollFlow.reject') }}
                </button>
                <button
                  v-if="canReverse && (accrual.status === 'approved' || accrual.status === 'exported')"
                  class="link-action danger"
                  type="button"
                  :disabled="busyId === accrual.id"
                  @click="pending = { action: 'reverse', accrual }"
                >
                  {{ t('finance.payrollFlow.reverse') }}
                </button>
                <span
                  v-if="
                    !(canApprove && accrual.status === 'provisional') &&
                    !(canReverse && (accrual.status === 'approved' || accrual.status === 'exported'))
                  "
                  class="empty-inline"
                >
                  —
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <span>{{ rangeLabel }}</span>
        <button class="btn-secondary" type="button" :disabled="page <= 1" @click="page = page - 1; load()">
          {{ t('finance.common.previous') }}
        </button>
        <button
          class="btn-secondary"
          type="button"
          :disabled="page >= totalPages"
          @click="page = page + 1; load()"
        >
          {{ t('finance.common.next') }}
        </button>
      </div>
    </template>
  </template>

  <ReasonPromptModal
    :open="Boolean(pending)"
    :title="pending?.action === 'reject' ? t('finance.payrollFlow.rejectTitle') : t('finance.payrollFlow.reverseTitle')"
    :message="pending?.action === 'reject' ? t('finance.payrollFlow.rejectMessage') : t('finance.payrollFlow.reverseMessage')"
    :busy="Boolean(busyId)"
    @close="pending = null"
    @confirm="confirmReason"
  />
</template>

<style scoped>
.status-provisional {
  border-color: #d9a441;
  color: #7a5510;
  background: #fffaf0;
}

.status-approved,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-exported {
  border-color: #7fa6d9;
  color: #274b7a;
  background: #f0f5fb;
}

.status-rejected,
.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
